import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';

/**
 * Reusable empty state component for displaying when there's no data
 * 
 * @param {Object} props
 * @param {string} props.title - Main message to display
 * @param {string} props.description - Optional secondary description
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.actionText - Text for the action button
 * @param {function} props.onAction - Function to call when action button is clicked
 * @param {boolean} props.disablePaper - Whether to disable the Paper wrapper
 * @param {Object} props.sx - Additional styles
 */
const EmptyState = ({
  title = 'No data found',
  description,
  icon,
  actionText,
  onAction,
  disablePaper = false,
  sx = {}
}) => {
  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 5,
        px: 3,
        ...sx
      }}
    >
      {icon && (
        <Box
          sx={{
            mb: 2,
            color: 'text.secondary',
            '& svg': {
              fontSize: 70
            }
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
          {description}
        </Typography>
      )}
      {actionText && onAction && (
        <Button variant="contained" onClick={onAction}>
          {actionText}
        </Button>
      )}
    </Box>
  );

  if (disablePaper) {
    return content;
  }

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderStyle: 'dashed'
      }}
    >
      {content}
    </Paper>
  );
};

export default EmptyState;
